import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ComponentProps } from 'react';

import { mutatePixelCreate } from '@/api/pixel';
import { LinkButton } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { Panel } from '@/components/panels';
import Prompt from '@/components/Prompt';
import { ReturnToLink } from '@/components/ReturnToLink';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import PixelForm, { getPixelFormDefaultValues, getPixelFormSchema } from '../PixelForm';

const PixelCreateContent = () => {
  const navigate = useInstanceNavigate();
  const { pushNotification } = useNotification();
  const form = useAppForm({
    defaultValues: getPixelFormDefaultValues(),
    validators: {
      onSubmit: getPixelFormSchema(),
      onSubmitAsync: async ({ value }) => {
        try {
          const { audience, channel, vendor, ...rest } = value;
          if (!audience || !channel || !vendor) {
            return;
          }

          await mutatePixelCreate({
            ...rest,
            channel_id: channel.id,
            vendor_id: vendor.id,
            audience_id: audience.id,
          });

          navigate(ROUTES.pixels.path, {
            state: {
              successMessage: t`Pixel was successfully created.`,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        }
      },
    },
  });

  const pixelForm = form as ComponentProps<typeof PixelForm>['form'];

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.AppForm>
        <form.Subscribe
          selector={(state) => ({ code: state.values.code, isSubmitting: state.isSubmitting })}
        >
          {({ code, isSubmitting }) => (
            <Prompt
              message={t`Are you sure you want to exit without publishing your new Pixel?`}
              shouldPreventNavigation={code != null && !isSubmitting}
            />
          )}
        </form.Subscribe>
        <div className="flex w-full flex-col gap-6">
          <div className="flex items-center justify-between">
            <ReturnToLink route={ROUTES.pixels.path} routeName={t`Pixel Overview`} />
            <div className="flex gap-4">
              <LinkButton to={ROUTES.pixels.path}>
                <Trans>Cancel</Trans>
              </LinkButton>
              <form.SubmitButton variant="primary">
                <Trans>Publish</Trans>
              </form.SubmitButton>
            </div>
          </div>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Panel title={<Trans>Create New Pixel</Trans>}>
                <PixelForm
                  form={pixelForm}
                  pixel={undefined}
                  disabled={isSubmitting}
                  isEdit={false}
                />
              </Panel>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  );
};

export default PixelCreateContent;
